import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext, useAsyncData } from '@/hooks';
import { api } from '@/api/client';
import type { SupportTicket } from '@/types';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/components/ui/Toast';
import { Icon } from '@/components/icons';
import styles from './AdminSupportPage.module.css';

const CATEGORY_LABELS: Record<string, string> = {
  TECHNICAL: 'admin.support.categoryTechnical',
  BILLING: 'admin.support.categoryBilling',
  TRAINING: 'admin.support.categoryTraining',
  DATA: 'admin.support.categoryData',
  OTHER: 'admin.support.categoryOther',
};

const CATEGORIES = Object.keys(CATEGORY_LABELS);

const STATUS_CLASSES: Record<SupportTicket['status'], string> = {
  OPEN: styles.statusOpen,
  IN_PROGRESS: styles.statusInProgress,
  RESOLVED: styles.statusResolved,
  CLOSED: styles.statusClosed,
};

const STATUS_LABELS: Record<SupportTicket['status'], string> = {
  OPEN: 'admin.support.open',
  IN_PROGRESS: 'admin.support.inProgress',
  RESOLVED: 'admin.support.resolved',
  CLOSED: 'admin.support.closed',
};

function formatDate(ts: string): string {
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return ts;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return ts;
  }
}

export function AdminSupportPage() {
  const { t } = useTranslation();
  const { activeContext } = useAppContext();
  const institutionId = activeContext?.institution?.id ?? '';

  const { state, reload } = useAsyncData<SupportTicket[]>(
    `admin-support-${institutionId}`,
    () => api.admin.support.listTickets(institutionId),
    !!institutionId,
  );

  const toast = useToast();
  const [form, setForm] = useState({ subject: '', description: '', category: 'TECHNICAL', productCode: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = useCallback(async () => {
    if (!form.subject.trim() || !form.description.trim()) return;
    setSubmitting(true);
    try {
      await api.admin.support.createTicket(institutionId, {
        subject: form.subject,
        description: form.description,
        category: form.category,
        productCode: form.productCode || undefined,
      });
      setForm({ subject: '', description: '', category: 'TECHNICAL', productCode: '' });
      toast.success(t('admin.support.ticketCreatedToast'));
      reload();
    } catch {
      // form keeps its values so the ticket text is not lost; submit can be retried
      toast.error(t('admin.support.ticketFailed'));
    } finally {
      setSubmitting(false);
    }
  }, [form, institutionId, reload, t, toast]);

  if (state.status === 'loading') {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>{t('admin.support.loading')}</div>
      </div>
    );
  }

  if (state.status === 'error') {
    const error = state.error;
    return (
      <div className={styles.page}>
        <Card className={styles.emptyState}>
          <Icon name={error.isMissingEndpoint ? 'info' : 'error'} size={48} />
          <h2 className={styles.emptyStateTitle}>{t('admin.support.title')}</h2>
          <p className={styles.emptyStateBody}>
            {t(error.isMissingEndpoint ? 'platform.failure.missing.body' : 'platform.failure.generic.body')}
          </p>
          <button className={styles.retryButton} onClick={reload}>{t('common.retry')}</button>
        </Card>
      </div>
    );
  }

  const tickets = state.data;

  return (
    <div className={styles.page}>
      <div className="page-header">
        <h1>{t('admin.support.title')}</h1>
        <p className="page-subtitle">{t('admin.support.subtitle')}</p>
      </div>

      <div className={styles.contentGrid}>
        <Card className={styles.sectionCard}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>{t('admin.support.createTicketTitle')}</h2>
          </div>
          <div className={styles.form}>
            <div className={styles.formField}>
              <label className={styles.formLabel} htmlFor="ticket-subject">{t('admin.support.subject')}</label>
              <input
                id="ticket-subject"
                className={styles.formInput}
                type="text"
                placeholder={t('admin.support.subjectPlaceholder')}
                value={form.subject}
                onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value }))}
              />
            </div>
            <div className={styles.formField}>
              <label className={styles.formLabel} htmlFor="ticket-description">{t('admin.support.description')}</label>
              <textarea
                id="ticket-description"
                className={styles.formTextarea}
                placeholder={t('admin.support.descriptionPlaceholder')}
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              />
            </div>
            <div className={styles.formField}>
              <label className={styles.formLabel} htmlFor="ticket-category">{t('admin.support.category')}</label>
              <select
                id="ticket-category"
                className={styles.formSelect}
                value={form.category}
                onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
              >
                {CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>{t(CATEGORY_LABELS[cat])}</option>
                ))}
              </select>
            </div>
            <div className={styles.formField}>
              <label className={styles.formLabel} htmlFor="ticket-product">{t('admin.support.affectedProduct')}</label>
              <input
                id="ticket-product"
                className={styles.formInput}
                type="text"
                placeholder={t('admin.support.productPlaceholder')}
                value={form.productCode}
                onChange={(e) => setForm((p) => ({ ...p, productCode: e.target.value }))}
              />
            </div>
            <div className={styles.formActions}>
              <Button
                onClick={handleSubmit}
                loading={submitting}
                loadingLabel={t('common.loading')}
                disabled={!form.subject.trim() || !form.description.trim()}
              >
                {t('admin.support.submitTicket')}
              </Button>
            </div>
          </div>
        </Card>

        <Card className={styles.sectionCard}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>{t('admin.support.existingTickets')}</h2>
          </div>
          {tickets.length === 0 ? (
            <p className={styles.emptyStateBody}>{t('admin.support.noTickets')}</p>
          ) : (
            <div className="table-scroll">
              <table className={styles.ticketTable}>
              <thead>
                <tr>
                  <th>{t('admin.support.ticketSubject')}</th>
                  <th>{t('admin.support.category')}</th>
                  <th>{t('admin.support.ticketStatus')}</th>
                  <th>{t('admin.support.ticketCreated')}</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map((ticket) => (
                  <tr key={ticket.id}>
                    <td>{ticket.subject}</td>
                    <td>{t(CATEGORY_LABELS[ticket.category] ?? '', ticket.category)}</td>
                    <td>
                      <span className={`${styles.statusBadge} ${STATUS_CLASSES[ticket.status] ?? styles.statusOpen}`}>
                        {t(STATUS_LABELS[ticket.status] ?? '', ticket.status)}
                      </span>
                    </td>
                    <td className={styles.ticketDate}>{formatDate(ticket.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
